import React, { useState, useEffect, useRef, useCallback } from 'react';
import { DocRenderer } from '@cyntler/react-doc-viewer';
import ViewerToolbar, { ViewMode } from './ViewerToolbar';
import { useViewerContainer } from './ViewerContainerContext';

const ImageViewer: DocRenderer = ({ mainState: { currentDocument } }) => {
  const viewerContext = useViewerContainer();
  const containerWidth = viewerContext.width;
  const containerHeight = viewerContext.height;
  const [viewMode, setViewMode] = useState<ViewMode>(viewerContext.initialViewMode || 'fit-width');
  const [zoom, setZoom] = useState<number>(800);

  // Cache native dimensions in context so they survive DocViewer's internal remounts during resize
  const dimensionCache = viewerContext.dimensionCache;
  const imgUri = currentDocument?.uri || currentDocument?.fileData || '';
  const cacheKey = typeof imgUri === 'string' ? imgUri : '';
  const cached = cacheKey ? dimensionCache?.get(cacheKey) : undefined;
  const [nativeWidth, setNativeWidth] = useState<number>(cached?.width || 0);
  const [nativeHeight, setNativeHeight] = useState<number>(cached?.height || 0);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const scrollRef = useRef<HTMLDivElement>(null);
  const [toolbarHeight, setToolbarHeight] = useState(0);

  // Measure toolbar height once on mount
  useEffect(() => {
    if (scrollRef.current?.previousElementSibling) {
      setToolbarHeight(scrollRef.current.previousElementSibling.getBoundingClientRect().height);
    }
  }, []);

  const scrollAreaHeight = containerHeight - (toolbarHeight || 40);

  // Load image to get native dimensions (skip if already cached)
  useEffect(() => {
    const uri = currentDocument?.uri || currentDocument?.fileData;
    if (!uri || typeof uri !== 'string') return;
    if (dimensionCache?.has(uri)) return;
    const img = new Image();
    img.onload = () => {
      dimensionCache?.set(uri, { width: img.naturalWidth, height: img.naturalHeight });
      setNativeWidth(img.naturalWidth);
      setNativeHeight(img.naturalHeight);
    };
    img.src = uri;
  }, [currentDocument?.uri, currentDocument?.fileData, dimensionCache]);

  const handleSetViewMode = useCallback((mode: ViewMode) => {
    setPosition({ x: 0, y: 0 });
    setViewMode(mode);
  }, []);

  // Compute display width based on view mode
  const availableWidth = containerWidth - 2;
  let displayWidth: number;
  if (viewMode === 'fit-width') {
    displayWidth = nativeWidth > 0 ? Math.min(availableWidth, nativeWidth) : availableWidth;
  } else if (viewMode === 'single-page') {
    if (nativeWidth > 0 && nativeHeight > 0) {
      const aspect = nativeWidth / nativeHeight;
      const widthConstrained = availableWidth;
      const heightConstrained = (scrollAreaHeight - 2) * aspect;
      displayWidth = Math.min(widthConstrained, heightConstrained, nativeWidth);
    } else {
      displayWidth = availableWidth;
    }
  } else {
    displayWidth = zoom;
  }

  const displayZoomPct = nativeWidth > 0 ? Math.round((displayWidth / nativeWidth) * 100) : 100;

  const zoomStep = nativeWidth > 0 ? nativeWidth * 0.25 : 200;
  const handleZoomOut = () => {
    const newWidth = Math.max(zoomStep, displayWidth - zoomStep);
    setZoom(newWidth);
    setViewMode('zoom');
  };
  const handleZoomIn = () => {
    const newWidth = Math.min(nativeWidth * 5, displayWidth + zoomStep);
    setZoom(newWidth);
    setViewMode('zoom');
  };
  const handleSetZoomPct = (pct: number) => {
    const newWidth = nativeWidth * (pct / 100);
    setZoom(newWidth);
    setViewMode('zoom');
  };

  // Wheel events: let the browser handle scrolling in all modes.
  // In single-page mode the container has overflow:hidden so nothing scrolls.
  const handleWheel = useCallback((_e: React.WheelEvent) => {}, []);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    setDragging(true);
    setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y });
  }, [position]);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!dragging) return;
    setPosition({ x: e.clientX - dragStart.x, y: e.clientY - dragStart.y });
  }, [dragging, dragStart]);

  const handleMouseUp = useCallback(() => setDragging(false), []);

  const isSinglePage = viewMode === 'single-page';
  const imgSrc = currentDocument?.fileData ?? currentDocument?.uri;

  if (!imgSrc) return null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: `${containerHeight}px` }}>
      <ViewerToolbar
        displayZoomPct={displayZoomPct}
        viewMode={viewMode}
        numPages={1}
        currentPage={1}
        onZoomIn={handleZoomIn}
        onZoomOut={handleZoomOut}
        onSetZoomPct={handleSetZoomPct}
        onSetViewMode={handleSetViewMode}
        onNavigatePage={() => {}}
        extra={viewerContext.toolbarExtra}
      />
      <div
        ref={scrollRef}
        onWheel={handleWheel}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        style={{
          overflow: isSinglePage ? 'hidden' : 'auto',
          flex: 1,
          background: '#e9ecef',
          display: 'flex',
          justifyContent: 'center',
          alignItems: isSinglePage ? 'center' : 'flex-start',
          cursor: dragging ? 'grabbing' : 'grab',
        }}
      >
        <img
          src={typeof imgSrc === 'string' ? imgSrc : undefined}
          alt="attachment"
          style={{
            width: `${displayWidth}px`,
            transform: viewMode === 'zoom' ? `translate(${position.x}px, ${position.y}px)` : undefined,
            boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
            userSelect: 'none',
            pointerEvents: 'none',
            flexShrink: 0,
            visibility: nativeWidth > 0 ? 'visible' : 'hidden',
          }}
          draggable={false}
        />
      </div>
    </div>
  );
};
ImageViewer.fileTypes = ['bmp', 'gif', 'jpg', 'jpeg', 'png', 'webp', 'svg',
  'image/bmp', 'image/gif', 'image/jpeg', 'image/png', 'image/webp', 'image/svg+xml'];
ImageViewer.weight = 2;

export default ImageViewer;
